<script>
import { mapState } from 'vuex';
import EmbedIframe from '@/components/EmbedIframe.vue';
import AppButton from '@/common/AppButton.vue';
import AppProgressbar from '@/common/AppProgressbar.vue';
import request from '@/services/request';
import { getCurrencySymbol } from '@/utils';
import { isGoalMeterActive } from '@/utils/goal-meter';
import css from '!!raw-loader!sass-loader!./widget.scss';
import buttonCss from '!!raw-loader!sass-loader!@/assets/css/checkout/app-button.scss';
import progressCss from '!!raw-loader!sass-loader!@/assets/css/checkout/app-progress-bar.scss';
import utilitiesCss from '!!raw-loader!sass-loader!@/assets/css/checkout/utilities.scss';

export default {
  name: 'CampaignCardWidget',
  components: { EmbedIframe, AppButton, AppProgressbar },
  props: {
    campaignSlug: { type: String, default: '' }
  },
  data: () => ({ campaign: null, imageFailed: false, css: buttonCss + progressCss + utilitiesCss + css }),
  computed: {
    ...mapState(['orgId']),
    ...mapState('publicOrgSettings', ['settings']),
    effectiveSlug() {
      return this.campaignSlug || this.settings.defaultSlug;
    },
    campaignSettings() {
      return this.campaign?.checkoutSettings || {};
    },
    theme() {
      return {
        '--primary-color': this.campaignSettings.themeColor || this.settings.themeColor || '#37215c',
        '--primary-color-text': this.campaignSettings.themeTextColor || this.settings.themeTextColor || '#ffffff'
      };
    },
    imageUrl() {
      return this.campaign?.pageFileUrls?.find((url) => !/\.(mp4|webm|mov)(?:[?#]|$)/i.test(url));
    },
    description() {
      const html = this.campaign?.pageDescription || this.campaignSettings.description || '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('script, style').forEach((element) => element.remove());
      doc.querySelectorAll('p, div, br, li').forEach((element) => element.append(' '));
      return doc.body.textContent.replace(/\s+/g, ' ').trim();
    },
    goalMeter() {
      return this.campaignSettings.goalMeter || {};
    },
    hasActiveGoalMeter() {
      return isGoalMeterActive(this.goalMeter);
    },
    progressValue() {
      if (this.goalMeter.type === 'donors') return Number(this.campaign.donorCount || 0);
      const amount = Number(this.campaign.chargeTotal || 0);
      if (this.goalMeter.type === 'units') {
        return this.goalMeter.unitCost > 0 ? Math.floor(amount / this.goalMeter.unitCost) : 0;
      }
      return amount;
    },
    progressTarget() {
      const key = { donors: 'donorsTarget', units: 'unitsTarget' }[this.goalMeter.type] || 'amountTarget';
      return Number(this.goalMeter[key] || 0);
    },
    progressPercentage() {
      return this.progressTarget > 0 ? Math.max(0, Math.min(100, (this.progressValue / this.progressTarget) * 100)) : 0;
    },
    progressLabel() {
      if (this.goalMeter.type === 'donors') return this.$t('goalMeter.donors');
      if (this.goalMeter.type === 'units') return this.goalMeter.unitName;
      return this.$t('goalMeter.raised');
    },
    currencySymbol() {
      return !['donors', 'units'].includes(this.goalMeter.type) ? getCurrencySymbol(this.campaign.currency) : '';
    },
    donateButtonText() {
      return this.campaignSettings.customCtaText?.trim() || this.$t(`donorWall.cta.${this.campaignSettings.ctaTerm?.toLowerCase() || 'donate'}`);
    }
  },
  watch: {
    effectiveSlug: {
      immediate: true,
      handler() {
        this.campaign = null;
        this.imageFailed = false;
        this.fetchCampaign();
      }
    }
  },
  methods: {
    async fetchCampaign() {
      const slug = this.effectiveSlug;
      if (!slug) return;
      try {
        const { data, error } = await request.get(`/public/campaign/${this.orgId}/${encodeURIComponent(slug)}`);
        if (!error && data?.id && data.slug === slug && slug === this.effectiveSlug) this.campaign = data;
      } catch (error) {
        console.error('Error fetching campaign card', error);
      }
    },
    donate() {
      window.Double.openCheckout({ campaign: this.effectiveSlug });
    }
  },
  mounted() {
    window.addEventListener('new-donations', this.fetchCampaign);
  },
  beforeDestroy() {
    window.removeEventListener('new-donations', this.fetchCampaign);
  }
};
</script>

<template>
  <div class="double--campaign-card-widget-frame">
    <embed-iframe v-if="campaign" :title="campaign.name" :css="css" height="519px" auto-height>
      <div class="campaign-card-container">
        <article class="campaign-card" :style="theme">
          <img v-if="imageUrl && !imageFailed" class="campaign-card__image" :src="imageUrl" alt="" @error="imageFailed = true" />
          <h2 class="campaign-card__name" dir="auto">{{ campaign.name }}</h2>
          <p v-if="description" class="campaign-card__description" dir="auto">{{ description }}</p>
          <div v-if="hasActiveGoalMeter" class="campaign-card__progress">
            <app-progressbar
              :value="progressPercentage"
              progress-color="var(--primary-color)"
              background-color="var(--gray-disabled)"
              role="progressbar"
              :aria-label="progressLabel"
              :aria-valuenow="progressPercentage"
              :aria-valuemin="0"
              :aria-valuemax="100"
            />
            <div class="campaign-card__stats">
              <strong>{{ currencySymbol }}{{ progressValue | number }} {{ progressLabel }}</strong>
              <span v-if="progressTarget > 0">{{ $t('goalMeter.outOf') }} {{ currencySymbol }}{{ progressTarget | number }}</span>
            </div>
          </div>
          <app-button type="button" class="w-full primary" @click="donate">
            <h3>{{ donateButtonText }}</h3>
          </app-button>
        </article>
      </div>
    </embed-iframe>
  </div>
</template>
